import { atom } from "jotai";

/**
 * Normalized profile fields surfaced to display-layer consumers
 * (Nav header, mobile sidebar, etc.). Decoupled from the underlying
 * auth-flow shape so callers no longer need to reach into either an
 * oidc-client-ts `User.profile` (Zitadel) or a synthesized profile
 * (WorkOS) and pick fields off it.
 *
 * - `displayName` is pre-computed by the bootstrap step (typically
 *   `${firstName} ${lastName}` trimmed) so consumers don't reimplement
 *   that fallback logic.
 * - `id` is the WorkOS user id on the WorkOS path and the Zitadel
 *   subject on the Zitadel path.
 * - `mongoId` is the user's own Mongo `_id`, surfaced from
 *   POST /user/UserInfo on the WorkOS path so the admin-table self-guards
 *   can match the admin's own row by `_id`. Zitadel logins never call
 *   UserInfo, so it stays undefined there and those guards rely on the
 *   legacy `zitadelId === profile.sub` comparison.
 */
export interface AuthProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  mongoId?: string;
}

/**
 * In-memory only — deliberately NOT `atomWithStorage`. PII in
 * localStorage is an XSS exfiltration surface. The cookie-based session
 * (WorkOS) and the persisted OIDC `userAtom` (Zitadel) are the sources
 * of truth on reload; `useAppBootstrap` re-populates this atom from
 * those each time the SPA boots.
 */
export const authProfileAtom = atom<AuthProfile | null>(null);
